import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTooth, FaHospital, FaMapMarkerAlt } from 'react-icons/fa';
import { MdMeetingRoom } from 'react-icons/md';

const HomeContainer = styled.div`
  padding-top: ${props => props.theme.sizes.headerHeight};
`;

const HeroSection = styled.section`
  height: 80vh;
  background: url('/dental-kid.jpg') center center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    background-position: 65% 20%;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0.4)
    );
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 0 20px;
  max-width: 800px;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-top: 60px;
  }
`;

const CTAButton = styled.a`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 15px 30px;
  border-radius: 30px;
  text-decoration: none;
  font-size: 18px;
  transition: background 0.3s;
  display: inline-block;

  &:hover {
    background: ${props => props.theme.colors.secondary};
    color: white;
  }
`;

const FeatureSection = styled.section`
  padding: 80px 20px;
  background: ${props => props.theme.colors.background};
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: ${props => props.theme.sizes.maxWidth};
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const FeatureCard = styled(motion.div)`
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.medium};

  svg {
    font-size: 3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 20px;
  }

  h3 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.5rem;
    margin-bottom: 15px;
  }

  p {
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
  }
`;

const TextLine = styled(motion.div)`
  overflow: hidden;
  margin-bottom: ${props => props.isTitle ? '30px' : '15px'};
  display: flex;
  justify-content: center;
`;

const TextContent = styled(motion.div)`
  h1 {
    font-size: 48px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: 36px;
    }
  }

  p {
    font-size: 20px;
    line-height: 1.6;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: 17px;
    }

    .mobile-break {
      display: none;
    }

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      .mobile-break {
        display: inline;
      }
      .desktop-break {
        display: none;
      }
    }
  }
`;

function Home() {
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12
      }
    }
  };

  const textVariants = {
    hidden: {
      opacity: 0,
      x: -20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const wordVariants = {
    hidden: {
      opacity: 0,
      x: -10
    },
    visible: {
      opacity: 1,
      x: 0
    }
  };

  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <TextLine isTitle>
              <TextContent variants={textVariants}>
                <h1>따뜻하고 정직한<span class="mobile-break"><br /></span>어린이 치과</h1>
              </TextContent>
            </TextLine>

            <TextLine>
              <TextContent variants={textVariants}>
                <p>소중한 우리아이가 경험하게 되는 처음치과</p>
              </TextContent>
            </TextLine>

            <TextLine>
              <TextContent variants={textVariants}>
                <p>서울쥬니어치과에서는 아이에게<span class="mobile-break"><br /></span> 좋은 기억을 남겨주고 싶습니다.</p>
              </TextContent>
            </TextLine>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            <CTAButton 
              href="tel:031-636-3360"
            >
              진료 예약하기
            </CTAButton>
          </motion.div>
        </HeroContent>
      </HeroSection>

      <FeatureSection>
        <FeatureGrid>
          <FeatureCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <FaTooth />
            <h3>어린이 전문</h3>
            <p>
              14년차 소아치과 전문의가<br />
              진료하는 어린이 전문치과입니다.
            </p>
          </FeatureCard>

          <FeatureCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <MdMeetingRoom />
            <h3>편안한 진료공간</h3>
            <p>
              아이들이 마음 놓을 수 있도록,<br />
              따뜻하게 구분된 공간에서 진료합니다.
            </p>
          </FeatureCard>

          <FeatureCard
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <FaMapMarkerAlt />
            <h3>접근성 좋은 위치</h3>
            <p>이천터미널 도보 5분거리<br />건물내 주차가능</p>
          </FeatureCard>
        </FeatureGrid>
      </FeatureSection>
    </HomeContainer>
  );
}

export default Home; 